import { makeAutoObservable } from 'mobx';
import { PagedList } from 'framework/api';
import { MobxManager } from 'framework/mobx-integration';
import { RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { DataSource } from 'areas/data-sources/types/dataSource';
import { DataHolder } from 'framework/data-loading/dataHolder';
import { GridDataItem } from 'controls/grid/gridDataItem';
import {
	DataSourceDataItem,
	getDataSourceData,
	getDataSourceFiltersConfiguration,
	getDataSources,
} from 'controls/designer/features/widgets/datasources/api';
import { DataSourceManagerConfig } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/types';

export class DataSourceManager {
	dataSources: DataHolder<DataSource[]>;
	rulesConfiguration: DataHolder<RulesConfiguration>;
	data: DataHolder<DataSourceDataItem[], PagedList<GridDataItem<DataSourceDataItem>>>;

	private readonly requestId: string;
	private mobx = new MobxManager();

	constructor(public config: DataSourceManagerConfig) {
		this.requestId = config.requestId;
		makeAutoObservable(this);

		this.dataSources = new DataHolder<DataSource[]>({
			request: () => {
				const dataSourceSelectorAccountId = this.config.selection.dataSourceSelectorAccountId;
				if (!this.config.loadDatasourceList || (!dataSourceSelectorAccountId && dataSourceSelectorAccountId !== null)) {
					return null;
				}

				return getDataSources({ accountId: this.config.selection.accountId, dataSourceSelectorAccountId });
			},
			initialValue: [],
		});

		this.rulesConfiguration = new DataHolder<RulesConfiguration>({
			request: () => {
				if (!this.config.selection.accountId || !this.config.selection.dataSourceId) return null;

				return getDataSourceFiltersConfiguration({
					dataSourceId: this.config.selection.dataSourceId,
					accountId: this.config.selection.accountId,
					requestId: this.requestId,
				});
			},
			initialValue: {},
		});

		this.data = new DataHolder<DataSourceDataItem[], PagedList<GridDataItem<DataSourceDataItem>>>({
			request: () => {
				if (!this.config.loadData) return null;

				const request = this.createGetDataSourceDataRequest();
				if (!request) return null;

				request.payload.filter = {};
				request.payload.sort = [];
				request.payload.skip = 0;
				request.payload.take = this.config.dataSizeLimit ?? 9999;

				return request;
			},
			initialValue: [],
			converter: (list) => list.items.map((x) => x.data),
		});

		this.mobx.reaction(
			() => this.config.selection.dataSourceSelectorAccountId,
			() => {
				this.config.selection.dataSourceId = null;
			},
		);

		this.mobx.reaction(
			() => this.config.selection.dataSourceId,
			() => {
				this.config.selection.rowKey = null;
			},
		);

		this.mobx.reaction(
			() => this.config.selection.rowKey,
			() => {
				this.config.selection.rowKeyValue = null;
			},
		);
	}

	get selectedDataSource() {
		return this.dataSources.data.find((x) => x.id == this.config.selection.dataSourceId);
	}

	get dataSourcesOptions() {
		return this.dataSources.data.map((x) => ({ label: x.name, value: x.id }));
	}

	get columnName() {
		return this.rulesConfiguration.data[this.config.selection.rowKey ?? '']?.label;
	}

	get columnOptions() {
		return Object.keys(this.rulesConfiguration.data ?? {}).map((key) => ({
			value: key,
			label: this.rulesConfiguration.data[key].label,
		}));
	}

	createGetDataSourceDataRequest() {
		if (!this.config.selection.accountId || !this.config.selection.dataSourceId) return null;

		return getDataSourceData({
			dataSourceId: this.config.selection.dataSourceId,
			accountId: this.config.selection.accountId,
			requestId: this.requestId,
		});
	}

	destroy = () => {
		this.mobx.destroy();
	};
}
